import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import {
  readSaveErrorMessage,
  type UpsaleBillDto,
  type UpsalePositionKind,
  type UpsalePrintStatus,
  type UpsalePromoResult,
  upsaleSalesTotals,
} from '@lite-workspace/dto-fotoclass-book';
import { environment } from '@environment';
import { SALES_DATA, type SalesPaymentRequest } from '../infrastructure/http-sales.data';

/** Core 409 on a tariff change: the student already paid the base position. */
const UPSALE_BASE_PAID = 'UPSALE_BASE_PAID';

/** Money of a bill the organizer collected and still holds; the server already took the handovers off. */
export function heldOf(bill: UpsaleBillDto): number {
  return bill.heldByOrganizer ?? 0;
}

/** Kinds that leave the print shop; mirrors core `UPSALE_PRINT_KINDS`. */
export const PRINT_KINDS: readonly UpsalePositionKind[] = ['base', 'copy', 'print'];

export const PRINT_STATUS_LABELS: Record<UpsalePrintStatus, string> = {
  paid: 'Оплачен',
  sent: 'В печати',
  ready: 'Готов к выдаче',
  issued: 'Выдан',
};

export interface PrintBadge {
  label: string;
  variant: 'acc-ink' | 'ok-ink';
  issued: boolean;
  /** Copies in hand: album + its copies + prints, by qty. */
  copies: number;
  /** Ready positions the organizer hands out; empty once issued. */
  positionIds: string[];
}

/** Hand-out state of a bill: ready positions win over issued ones; none of either = no badge. */
export function printBadge(bill: UpsaleBillDto | undefined): PrintBadge | null {
  const positions = bill?.positions ?? [];
  const ready = positions.filter(p => p.printStatus === 'ready');
  const shown = ready.length > 0 ? ready : positions.filter(p => p.printStatus === 'issued');
  if (shown.length === 0) return null;
  const copies = shown.reduce((n, p) => n + p.qty, 0);
  if (ready.length === 0) {
    return { label: `${PRINT_STATUS_LABELS.issued} · ${copies} экз.`, variant: 'ok-ink', issued: true, copies, positionIds: [] };
  }

  return {
    label: copies > 1 ? `${PRINT_STATUS_LABELS.ready} · ${copies} экз.` : PRINT_STATUS_LABELS.ready,
    variant: 'acc-ink',
    issued: false,
    copies,
    positionIds: ready.map(p => p.id),
  };
}

interface SalesState {
  projectId: string | null;
  bills: UpsaleBillDto[];
  loaded: boolean;
  failed: boolean;
}

const initialState: SalesState = { projectId: null, bills: [], loaded: false, failed: false };

/** Bills of the class on the «Покупки» tab; the server list is the only truth. */
export const SalesStore = signalStore(
  environment.storeWithDevTools('upsale-sales'),
  withState(initialState),
  withComputed(store => ({
    totals: computed(() => upsaleSalesTotals(store.bills())),
  })),
  withMethods(store => {
    const data = inject(SALES_DATA);
    let generation = 0;

    async function load(projectId: string): Promise<void> {
      const gen = ++generation;
      if (store.projectId() !== projectId) {
        updateState(store, '[Sales] Switch Project', { ...initialState, projectId });
      }

      try {
        const bills = await data.list(projectId);
        if (gen === generation) updateState(store, '[Sales] Loaded', { bills, loaded: true, failed: false });
      } catch {
        if (gen === generation) updateState(store, '[Sales] Load Failed', { loaded: true, failed: true });
      }
    }

    /** After a write: the tab moved to another project meanwhile → no read, else it would paint this one under it. */
    async function reload(projectId: string): Promise<void> {
      if (store.projectId() !== projectId) return;
      await load(projectId);
    }

    return {
      load,
      /** Marks the payment, then reloads the class: tiles and rows recompute from the server list. */
      async pay(projectId: string, personId: string, req: SalesPaymentRequest): Promise<UpsaleBillDto> {
        const bill = await data.pay(projectId, personId, req);
        await reload(projectId);
        return bill;
      },
      /** Marks the organizer's handover, then reloads: «У организатора» shrinks, «Получено» grows. */
      async handover(projectId: string, amount: number, idempotencyKey: string, note?: string): Promise<void> {
        await data.handover(projectId, amount, idempotencyKey, note);
        await reload(projectId);
      },
      /** Sets the photographer discount of one bill, then reloads: «К оплате» of the row and tile follow. */
      async setDiscount(projectId: string, personId: string, discount: number): Promise<void> {
        await data.setDiscount(projectId, personId, discount);
        await reload(projectId);
      },
      /** Applies a promo code to one bill of the open project; reloads when applied. `null` = the request failed. */
      async applyPromo(personId: string, code: string): Promise<UpsalePromoResult | null> {
        const projectId = store.projectId();
        if (!projectId) return null;
        try {
          const result = await data.applyPromo(projectId, personId, code.trim());
          if (result.applied) await reload(projectId);
          return result;
        } catch {
          return null;
        }
      },
      /** Sets the package of one bill (`null` = base album), then reloads; `false` = base paid, server keeps it. Else throws. */
      async setTariff(personId: string, tariff: string | null): Promise<boolean> {
        const projectId = store.projectId();
        if (!projectId) throw new Error('SalesStore: no project loaded');
        try {
          await data.setTariff(projectId, personId, tariff);
        } catch (error) {
          const basePaid = error instanceof HttpErrorResponse && error.status === 409;
          if (basePaid && readSaveErrorMessage(error).includes(UPSALE_BASE_PAID)) return false;
          throw error;
        }

        await reload(projectId);
        return true;
      },
      /** Moves print positions in ONE request (all or nothing), then reloads the class. */
      async setPrintStatus(projectId: string, positionIds: string[], status: UpsalePrintStatus): Promise<void> {
        await data.setPrintStatus(projectId, positionIds, status);
        await reload(projectId);
      },
      /** Reminds every debtor; bills do not change, so no reload. Resolves the number of students reached. */
      async remind(projectId: string): Promise<number> {
        return (await data.remind(projectId)).count;
      },
    };
  }),
);
